/*
 * Fix Alignment panel (AIVA-5381).
 *
 * The mockup surfaces this as a one-shot chat action pill: a progress bubble
 * ("Fixing alignment…" → the steps it walks) followed by a confirmation of how
 * many shapes moved. This is that same flow given its own Deckorate card, so
 * the layout fix is reachable without going through chat: pick the scope, press
 * the button, watch the steps, keep or revert the result.
 */

import { useCallback } from "react";
import { ModuleHeader } from "../../../../../shared/components/ModuleHeader";
import { useFixAlignment, type FixAlignmentPhase } from "../hooks/useFixAlignment";
import { reportFixAlignmentFailure, type AlignmentMode } from "../services/fixAlignmentApi";
import type { AlignmentScope } from "../services/slideGeometry";
import styles from "./FixAlignmentPanel.module.css";

interface FixAlignmentPanelProps {
  readonly onBack?: () => void;
}

/** The steps the mockup's progress bubble walks, in order. */
const PHASES: ReadonlyArray<{ readonly phase: FixAlignmentPhase; readonly label: string }> = [
  { phase: "reading", label: "Reading the slide's shapes" },
  { phase: "analysing", label: "Finding overlaps and uneven spacing" },
  { phase: "applying", label: "Nudging shapes into line" },
];

const SCOPES: ReadonlyArray<{ readonly scope: AlignmentScope; readonly label: string }> = [
  { scope: "all", label: "Whole slide" },
  { scope: "selected", label: "Selected shapes" },
];

const MODE_LABEL: Record<AlignmentMode, string> = {
  fast: "Deterministic pass",
  ai: "AI layout pass",
};

function shapesLabel(count: number): string {
  return `${count} shape${count === 1 ? "" : "s"}`;
}

/** Where a step sits relative to the one currently running. */
type StepState = "done" | "active" | "waiting";

function stepStateAt(index: number, currentIndex: number): StepState {
  if (index < currentIndex) return "done";
  return index === currentIndex ? "active" : "waiting";
}

const STEP_CLASS: Record<StepState, string | undefined> = {
  done: styles.stepDone,
  active: styles.stepActive,
  waiting: styles.stepWaiting,
};

function StepMark({ state }: Readonly<{ state: StepState }>) {
  if (state === "done") return <>✓</>;
  if (state === "active") return <span className={styles.spinner} />;
  return <>○</>;
}

/** The progress list — completed steps tick, the current one spins, the rest wait. */
function ProgressSteps({ current }: Readonly<{ current: FixAlignmentPhase }>) {
  const currentIndex = PHASES.findIndex((step) => step.phase === current);
  return (
    <ol className={styles.steps} aria-label="Fix alignment progress">
      {PHASES.map((step, index) => {
        const state = stepStateAt(index, currentIndex);
        return (
          <li
            key={step.phase}
            className={`${styles.step} ${STEP_CLASS[state]}`}
            aria-current={state === "active" || undefined}
          >
            <span className={styles.stepMark} aria-hidden="true">
              <StepMark state={state} />
            </span>
            <span>{step.label}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function FixAlignmentPanel({ onBack }: Readonly<FixAlignmentPanelProps>) {
  const { state, scope, setScope, run, cancel, revert } = useFixAlignment();
  const isRunning = state.status === "running";

  const handleRunClick = useCallback(() => {
    run().catch(reportFixAlignmentFailure("fix-alignment-run-ui"));
  }, [run]);

  const handleRevertClick = useCallback(() => {
    revert().catch(reportFixAlignmentFailure("fix-alignment-revert-ui"));
  }, [revert]);

  return (
    <div className={styles.panel}>
      <ModuleHeader title="Fix Alignment" subtitle="Deckorate" onBack={onBack} />
      <div className={styles.body}>
        <p className={styles.desc}>
          Reviews the slide's layout and nudges shapes into line — fixing rows and columns, evening
          up spacing, and clearing overlaps and overflow.
        </p>

        {/* fieldset/legend rather than a div with role="group" — the native
            grouping semantics are better supported by assistive tech. */}
        <fieldset className={styles.scopeGroup}>
          <legend className={styles.label}>Apply to</legend>
          <div className={styles.seg}>
            {SCOPES.map((option) => (
              <button
                key={option.scope}
                type="button"
                className={scope === option.scope ? `${styles.segBtn} ${styles.segBtnOn}` : styles.segBtn}
                onClick={() => setScope(option.scope)}
                disabled={isRunning}
                aria-pressed={scope === option.scope}
              >
                {option.label}
              </button>
            ))}
          </div>
          <p className={styles.hint}>
            {scope === "all"
              ? "Every shape on the current slide is considered."
              : "Only the shapes selected in PowerPoint move — select at least two."}
          </p>
        </fieldset>

        <button type="button" className={styles.runBtn} onClick={handleRunClick} disabled={isRunning}>
          {isRunning ? "Fixing alignment…" : "Fix Alignment"}
        </button>

        {isRunning && (
          <>
            <ProgressSteps current={state.phase} />
            <button type="button" className={styles.cancelBtn} onClick={cancel}>
              Cancel
            </button>
          </>
        )}

        {state.status === "done" && (
          <div className={`${styles.statusBox} ${styles.statusSuccess}`}>
            <span className={styles.statusText}>
              Auto-alignment: {shapesLabel(state.adjusted)} adjusted.
            </span>
            <span className={styles.modeChip}>{MODE_LABEL[state.mode]}</span>
            {state.canRevert && (
              <button type="button" className={styles.revertBtn} onClick={handleRevertClick}>
                Revert
              </button>
            )}
          </div>
        )}

        {state.status === "no-changes" && (
          <div className={`${styles.statusBox} ${styles.statusInfo}`}>
            <span className={styles.statusText}>
              Nothing to fix — these shapes are already aligned.
            </span>
          </div>
        )}

        {state.status === "cancelled" && (
          <div className={`${styles.statusBox} ${styles.statusInfo}`}>
            <span className={styles.statusText}>Cancelled — nothing was changed.</span>
          </div>
        )}

        {state.status === "reverted" && (
          <div className={`${styles.statusBox} ${styles.statusInfo}`}>
            <span className={styles.statusText}>
              Reverted — {shapesLabel(state.adjusted)} put back where they were.
            </span>
          </div>
        )}

        {state.status === "error" && (
          <div className={`${styles.statusBox} ${styles.statusError}`}>
            <span className={styles.statusLabel}>Failed:</span>
            <span className={styles.statusText}>{state.message}</span>
          </div>
        )}
      </div>
    </div>
  );
}
